import React, { useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft'
import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import {
  Box,
  Button,
  IconButton,
  Skeleton,
  styled,
  Typography,
} from '@mui/material'
import { isEmpty, map } from 'lodash'
import PropTypes from 'prop-types'

import { ZoomIcon } from '../icons'

import ImageModal from './ImageModal'

const StyledArrowBtn = styled((props) => (
  <IconButton disableRipple disableFocusRipple {...props} />
))(({ theme }) => ({
  minWidth: 'unset',
  width: 44,
  height: 80,
  padding: theme.spacing(1),
  color: theme.palette.primary.contrastText,
  background: theme.palette.primary.main,
  borderRadius: 0,
}))

function ImageCarousel({ images }) {
  const { t } = useTranslation()
  const [activeStep, setActiveStep] = useState(0)
  const [modalImage, setModalImage] = useState({})
  const [isModalOpen, setIsModalOpen] = useState(false)
  const maxSteps = images.length
  const sliderRef = useRef(null)

  useEffect(() => {
    if (!isEmpty(images)) {
      const { current } = sliderRef
      const { firstChild, parentElement } = current
      const visibleThumbsCounter = Math.floor(
        parentElement.clientWidth / firstChild.clientWidth,
      )
      const activeThumb = activeStep + 1

      if (activeThumb === maxSteps && maxSteps > visibleThumbsCounter) {
        current.style.left = `${
          parentElement.clientWidth - current.clientWidth
        }px`
      } else if (activeThumb > visibleThumbsCounter) {
        current.style.left = `-${
          (activeThumb - visibleThumbsCounter) * firstChild.clientWidth
        }px`
      } else {
        current.style.left = 0
      }
    }
  }, [activeStep, images, maxSteps])

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1)
  }

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1)
  }

  const handleSelect = (key) => () => {
    setActiveStep(key)
  }

  const handleModalOpen = (image) => () => {
    setIsModalOpen(true)
    setModalImage(image)
  }

  const handleModalClose = () => {
    setIsModalOpen(false)
    setModalImage({})
  }

  return isEmpty(images) ? (
    <Skeleton variant="rectangular" width="100%" height={400} />
  ) : (
    <>
      <Box
        sx={({ palette }) => ({
          position: 'relative',
          width: '100%',
          height: 400,
          backgroundColor: palette.gray.dark,
          backgroundImage: `url(${images[activeStep].url})`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        })}
      >
        <IconButton
          size="small"
          disableRipple
          disableFocusRipple
          onClick={handleModalOpen({
            title: images[activeStep].name,
            imageUrl: images[activeStep].url,
          })}
          sx={({ palette, spacing }) => ({
            position: 'absolute',
            top: spacing(2),
            right: spacing(2),
            borderRadius: 0,
            backgroundColor: palette.common.black,
          })}
        >
          <ZoomIcon />
        </IconButton>
      </Box>
      <Box sx={{ p: 1 }}>
        <Typography align="center">
          <strong>{images[activeStep].name}</strong>
        </Typography>
        <Typography align="center" variant="body2">
          {t('controlsPhotoCounter', { count: activeStep + 1, max: maxSteps })}
        </Typography>
      </Box>
      <Box sx={{ display: 'flex' }}>
        <StyledArrowBtn disabled={activeStep === 0} onClick={handleBack}>
          <ChevronLeftIcon />
        </StyledArrowBtn>
        <Box sx={{ position: 'relative', flexGrow: 1, overflow: 'hidden' }}>
          <Box
            ref={sliderRef}
            sx={{
              position: 'absolute',
              left: 0,
              display: 'flex',
              flexDirection: 'row',
              flexWrap: 'nowrap',
              flexGrow: 1,
              transition: 'left 250ms ease-in-out',
            }}
          >
            {map(images, (image, imgKey) => (
              <Box key={imgKey} sx={{ pl: 1 }}>
                <Button
                  className={imgKey === activeStep ? 'Mui-active' : ''}
                  onClick={handleSelect(imgKey)}
                  sx={({ palette }) => ({
                    p: 0,
                    height: 80,
                    width: 140,
                    backgroundSize: 'cover',
                    backgroundImage: `url(${image.url})`,
                    backgroundColor: palette.common.black,
                    transition: 'none',
                    '&.Mui-active': {
                      border: `4px solid ${palette.common.brand}`,
                      pointerEvents: 'none',
                    },
                  })}
                />
              </Box>
            ))}
          </Box>
        </Box>
        <StyledArrowBtn
          disabled={activeStep === maxSteps - 1}
          onClick={handleNext}
        >
          <ChevronRightIcon />
        </StyledArrowBtn>
      </Box>
      <ImageModal
        title={modalImage.title}
        imageUrl={modalImage.imageUrl}
        isOpen={isModalOpen}
        onClose={handleModalClose}
      />
    </>
  )
}

ImageCarousel.propTypes = {
  images: PropTypes.array.isRequired,
}

export default ImageCarousel
